import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { StraightenerRecipeVersion } from "../models/StraightenerRecipe";

// Matches MILL.StraightenerRecipes.Name.
const NAME_MAX = 50;

interface Props {
	open: boolean;
	recipe: StraightenerRecipeVersion | null;
	// Names already used by the source product's recipes (lower-cased). The copy
	// lands on that same product, where names are unique.
	existingNames?: string[];
	isDuplicating?: boolean;
	onDuplicate: (recipeId: number, name?: string) => void;
	onCancel: () => void;
}

// First free "<source> (copy)" / "(copy 2)" … for the product, trimmed to fit
// the column. The root shrinks instead of the suffix, so the candidates stay
// distinct even when the source name is already at the limit.
const suggestName = (base: string, taken: Set<string>): string => {
	const root = (base || "Recipe").trim();
	for (let n = 1; n <= 99; n++) {
		const suffix = n === 1 ? " (copy)" : ` (copy ${n})`;
		const candidate = root.slice(0, NAME_MAX - suffix.length) + suffix;
		if (!taken.has(candidate.toLowerCase())) return candidate;
	}
	return "";
};

// Mirrors configuration/recipes DuplicateRecipeModal: clones the selected
// version into a new version of the same product, under a new name.
export const DuplicateStraightenerRecipeModal = ({
	open,
	recipe,
	existingNames,
	isDuplicating = false,
	onDuplicate,
	onCancel,
}: Props) => {
	const [name, setName] = useState("");
	// Name errors stay hidden until the first Duplicate attempt.
	const [submitAttempted, setSubmitAttempted] = useState(false);

	const taken = useMemo(() => new Set(existingNames ?? []), [existingNames]);

	// Seed with a name that already passes the uniqueness rule, so the common
	// case is one click.
	useEffect(() => {
		if (open) {
			setName(suggestName(recipe?.Name ?? "", taken));
			setSubmitAttempted(false);
		}
	}, [open, recipe?.Name, taken]);

	const trimmed = name.trim();
	const nameError = !trimmed
		? "Recipe name is required."
		: taken.has(trimmed.toLowerCase())
			? "A recipe with this name already exists for this product."
			: null;

	if (!recipe) return null;

	const productLabel = recipe.ProductTypeName
		? `(${recipe.ProductTypeName}) ${recipe.ProductName}`
		: recipe.ProductName;

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>DUPLICATE RECIPE</DialogTitle>
			<DialogContent>
				<Grid2 container spacing={1.5} sx={{ mt: 0.5 }}>
					<Grid2 size={{ xs: 12 }}>
						<Typography variant="body2" color="text.secondary">
							Source: <strong>{recipe.Name || `v${recipe.Version}`}</strong> -{" "}
							{productLabel}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							label="New Name"
							required
							value={name}
							onChange={(e) => setName(e.target.value)}
							error={submitAttempted && !!nameError}
							helperText={(submitAttempted && nameError) || undefined}
							fullWidth
							size="small"
							slotProps={{
								inputLabel: { shrink: true },
								htmlInput: { maxLength: NAME_MAX },
							}}
						/>
					</Grid2>
				</Grid2>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onCancel}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					color="primary"
					variant="contained"
					onClick={() => {
						setSubmitAttempted(true);
						if (!nameError) onDuplicate(recipe.Id, trimmed);
					}}
					disabled={isDuplicating}
				>
					{isDuplicating ? "DUPLICATING..." : "DUPLICATE"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
